import React from "react";
import { miyagi } from "ldrs";
import warningIcon from "../../../assets/imgs/delete_account.svg";
import congratulations from "../../../assets/imgs/congratulations.png";

// Define type for sync status
export type SyncStatus =
  | "NOT_STARTED"
  | "PENDING"
  | "IN_PROGRESS"
  | "COMPLETED"
  | "FAILED_TO_PROCESS"
  | "FAILED_TO_SYNC";

// SyncStatusRenderer component to handle rendering based on status
export interface SyncStatusRendererProps {
  status: SyncStatus | undefined;
  provider: string;
  onGetStarted: () => void;
  onTryAgain: () => void;
}

const SyncStatusRenderer: React.FC<SyncStatusRendererProps> = ({
  status,
  provider,
  onGetStarted,
  onTryAgain,
}) => {
  const renderBottomButton = (text: string, onClick: () => void) => (
    <div
      onClick={onClick}
      className="fixed bottom-0 left-0 right-0 px-6"
      style={{
        paddingBottom: "calc(env(safe-area-inset-bottom) + 25px)",
      }}
    >
      <button className="bg-[#00E0FF] w-full rounded-[50px] flex items-center justify-center h-[60px]">
        <span className="text-[14px] font-medium text-black">{text}</span>
      </button>
    </div>
  );

  switch (status) {
    case "COMPLETED":
    case "PENDING":
    case "IN_PROGRESS":
    case "NOT_STARTED":
      return (
        <div className="w-full h-screen bg-[#000513]">
          <div className="flex flex-col h-full justify-center items-center">
            <div className="text-center">
              <div className="flex-1 flex items-center w-[313px] h-[300px]">
                <img src={congratulations} alt="congratulations" />
              </div>
              <div className="flex flex-col gap-1 px-3">
                <span className="text-white text-[36px] font-medium">
                  Congratulations
                </span>
                <span className="text-[16px] text-white/70 font-normal">
                  Your league is now synced.
                </span>
              </div>
            </div>
          </div>
          {renderBottomButton("Continue", onGetStarted)}
        </div>
      );
    case "FAILED_TO_PROCESS":
    case "FAILED_TO_SYNC":
      return (
        <div className="w-full h-screen bg-[#000513]">
          <div className="flex flex-col h-full justify-center items-center">
            <img
              src={warningIcon}
              alt="warning"
              className="w-[50px] h-[50px]"
            />
            <div className="text-center mt-4">
              <div className="flex flex-col gap-1 px-3">
                <span className="text-white text-[24px] font-medium">
                  Sync Failed
                </span>
                <span className="text-[16px] text-[#FF0000] font-semibold mt-2">
                  {status === "FAILED_TO_PROCESS"
                    ? "We couldn't process your league data"
                    : "Failed to sync your league"}
                </span>
              </div>
            </div>
          </div>
          {renderBottomButton("Try Again", onTryAgain)}
        </div>
      );
    default:
      return (
        <div className="w-full h-screen bg-[#000513]">
          <div className="flex flex-col h-full justify-center items-center">
            <div className="text-center">
              <div className="flex flex-col gap-1 px-3">
                <span className="text-white text-[24px] font-medium">
                  Sync Status Unknown
                </span>
                <span className="text-[16px] text-[#FF0000] font-normal">
                  We are having trouble determining the status of your sync
                </span>
              </div>
            </div>
          </div>
          {renderBottomButton("Check Again", onTryAgain)}
        </div>
      );
  }
};

miyagi.register();
export default SyncStatusRenderer;
